import { DownOutlined, LeftOutlined, RightOutlined, SafetyOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Flex, Input, Modal, Row, Select, Space, Spin, Table, Tooltip, Typography, theme } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { countryToRegionMap, GEO_LIST_TREE_STRUCTURE, region_title_index_map } from "../../../../constants";
import { IAB_CHOICES_SYSTEM_IDS, LEGALBASIS_OPTIONS, renderInfoTip, USNAT_CODE, CONSENT_SCOPE_COLUMN_HEADER_TOOLTIP } from "../helper";
import { useDebouncedSearch } from "../../../../hooks";
import RespectGpcModal from "../NewPrivacyRegulation/Steps/PrivacyChoices/RespectGpcModal";
import { useSelector } from "react-redux";
import AddRegionsModal from "./AddRegionsModal";

const {Title, Text, Paragraph} = Typography;

const RegionMappingGlobalStandard = (props) => {
  const [categories, setCategories] = useState(props.usPrivacyRegulation.categories ?? []);
  const [privacySignalConfigByGeo, setPrivacySignalConfigByGeo] = useState(props.usPrivacyRegulation.configByGeo);
  const [regionCategoryMapping, setRegionCategoryMapping] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [openRespectGpcModal, setOpenRespectGpcModal] = useState(false);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(true);
  const [tableState, setTableState] = useState([]);
  const [showAddRegionsModal, setShowAddRegionsModal] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const systemPurposes = useSelector(state => state.usPrivacyReducerState.getIn(['purposes', 'value']))
  const systemPurposesPending = useSelector(state => state.usPrivacyReducerState.getIn(['purposes', 'pending']));
  const { token } = theme.useToken();
  const [modal, contextHolder] = Modal.useModal();

  const PARENT_REGION_TITLES = {
    "REGION_EEA": "Europe",
    "REGION_OTHER": "Other",
    "CA": "Canada",
    "US": "United States",    
  };

  useEffect(() => {
    props.usPrivacyRegulation.categories = categories;
    props.usPrivacyRegulation.configByGeo = privacySignalConfigByGeo;
    if(props.resetValidationError) props.resetValidationError();
  }, [categories, privacySignalConfigByGeo])

  function getIabDefinedConfigGeo(cat, region, privacySignal) {
    const iabPurposeOriginal = systemPurposes.find((purpose) => purpose.purposeRef.id === cat.purposeRef?.id)
    let regionCode = region;
    if((privacySignal ?? privacySignalConfigByGeo?.[region]?.legislation) === "usnat") {
      regionCode = USNAT_CODE
    }
    const iabPurOrgConfigGeo = iabPurposeOriginal?.configByGeo[regionCode]
    return iabPurOrgConfigGeo;
  };

  async function handlePrivacySignalChange(regionCode, privacySignalValue, isState) {
    const parentRegionCode = countryToRegionMap[regionCode?.replace("US_", "STATE_")];
    const isRegionalScope = parentRegionCode.includes("REGION_") ? 
      privacySignalValue === "regional" : 
      privacySignalValue === "usnat";

    const modalContent = isRegionalScope
      ? "Changing to group level consent means previous individual settings will be overridden and users will be treated as new users under the group."
      : "Changing to individual level consent means users will be treated as new users in the respective region.";

    const confirmation = await modal.confirm({
      title: "Change Consent Scope",
      content: modalContent,
      okText: "Apply",
      width: 500
    });
    if(confirmation) {
      setPrivacySignalConfigByGeo(prevConfig => {
        const newConfig = {...prevConfig}; // Create a new object with spread

        newConfig[regionCode] = {legislation: privacySignalValue}
        return newConfig; // Return the new object
      });
      if(isState ? privacySignalValue === "usnat" : privacySignalValue === "regional") {
        setCategories((categories) => categories.map((cat) => {  
          if(cat.configByGeo[regionCode]) {
            // cat.configByGeo[regionCode?.split("_", 1)[0]] = cat.configByGeo[regionCode];
            delete cat.configByGeo[regionCode];
          }
          let parentRegionCode = countryToRegionMap[regionCode?.replace("US_", "STATE_")];
          if(!cat.configByGeo[parentRegionCode]) {
            cat.configByGeo[parentRegionCode] = {defaultLegalBasis: "NOT_APPLICABLE"}
          }
          return cat;
        }))
      } else {
        setCategories((categories) => categories.map((cat) => {
          let iabConfigGeo = getIabDefinedConfigGeo(cat, regionCode, "state");
          cat.configByGeo[regionCode] = iabConfigGeo ?? {defaultLegalBasis: "NOT_APPLICABLE"}
          return cat;
        }))
      }
    }
  }

  async function handleLegalBasisChange(categoryId, regionCode, legalBasisValue, parentCode) {
    if(parentCode) {
      let prefixCode = "";
      if(parentCode === "EEA" || parentCode === "OTHER") {
        prefixCode = "REGION_";
      }
      setCategories((categories) => categories.map((cat) => {
        if(cat.id === categoryId) {
          cat.configByGeo[`${prefixCode}${regionCode}`] = {defaultLegalBasis: legalBasisValue};
        } 
        return cat;
      }))
    }else {
      setCategories((categories) =>categories.map((cat) => {
        if(cat.id === categoryId) {
          cat.configByGeo[regionCode] = {defaultLegalBasis: legalBasisValue};
        } 
        return cat;
      }))
    }
  }

  const columns = [
    {
      title: "Region",
      fixed: "left",
      dataIndex: "region",
      width: 200,
      sorter: (a,b) => a.title?.localeCompare(b.title),
      render: (stateCode, record) => {
        return (<Text>{record.title}</Text>);
      },
    },
    {
      title: <>Consent Scope {renderInfoTip(CONSENT_SCOPE_COLUMN_HEADER_TOOLTIP)}</>,
      fixed: "left",
      dataIndex: "privacySignal",
      width: 200,
      filters: [
        {text: "Group Level", value: "group"},
        {text: "Individual Level", value: "individual"}
      ],
      onFilter: (value, record) =>{
        const regionCode = countryToRegionMap[record.region?.replace("US_", "STATE_")];
        let isState = ["US", "CA"].includes(record.children ? record.region : regionCode);
        let compareValue = ""
        if(value == "group"){
          compareValue = isState ? "usnat" : "regional";
        } else {
          compareValue = isState ? "state" : "usnat";
        }
        return (!record.children ? record.privacySignal === compareValue : false) || record?.children?.some((childRecord) => childRecord.privacySignal === compareValue);
      },
      filterMultiple: false,
      render: (value, record) => {
        const regionCode = countryToRegionMap[record.region?.replace("US_", "STATE_")];
        let isState = ["US", "CA"].includes(record.children ? record.region : regionCode);
        if(record.children) {
          if(record.children.every((child) => child.privacySignal === (isState ? "usnat" : "regional"))) {
            return "Group level"
          }
          return "Multi-select";
        }
        let options = [
          {
            label: "Group Level",
            value: isState ? "usnat" : "regional"
          },
          {
            label: "Individual Level",
            value: isState ? "state" : "usnat"
          }
        ];
        return (
          <Select
            value={value}
            defaultValue={"usnat"}
            placeholder="Select"
            options={options}
            disabled={props.readOnly}
            onChange={(value) => handlePrivacySignalChange(record.region, value, isState)}
            style={{minWidth: 150}}
          />
        )
      }
    }
  ]

  const legalBasisIndexMap = useMemo(() => LEGALBASIS_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {}),[LEGALBASIS_OPTIONS]);

  categories.forEach((cat) => {
    // const isIabCat = cat.type === "SYSTEM_PURPOSE"
    // const options = isIabCat 
    //   ? LEGALBASIS_OPTIONS.filter(opt => opt.value !== "DISCLOSURE_ONLY")
    //   : LEGALBASIS_OPTIONS;
    columns.push({
      title: (
        <Space direction="vertical">
          <Paragraph style={{maxWidth: 150}} ellipsis={{tooltip:{title: cat.privacyChoice ?? cat.name}, rows: 2}}>{cat.privacyChoice ?? cat.name}</Paragraph>
        </Space>
      ),
      dataIndex: ["legalBasis", cat.id],
      width: 200,
      sorter: (a,b) => {
        const legalBasisOrder = LEGALBASIS_OPTIONS.reduce((acc, option, index) => {
          acc[option.value] = index;
          return acc;
        }, {});
        const aOrder = legalBasisOrder[a.legalBasis[cat.id]] ?? 3; // 3 is the index for not_applicable
        const bOrder = legalBasisOrder[b.legalBasis[cat.id]] ?? 3;
        return aOrder - bOrder;
      },
      showSorterTooltip: {target: 'sorter-icon'},
      render: (value, record) => {
        let isDisabled = false;
        let orgIabConfig = null;
        let showTooltip = true;
        let isState = record.children ? (record.region === "US" || record.region === "CA") : (record.region.includes("US_") || record.region.includes("CA_"));
        if((!record.children) && (isState ? record.privacySignal === "usnat" : record.privacySignal === "regional")) {
          isDisabled = true;
        }
        if(record.children && record.children.every((child) => child.privacySignal === (isState ? "state" : "usnat"))) {
          isDisabled = true;
          showTooltip = false;
        }
        if(isDisabled || props.readOnly) {
          return (
            <div className="select-container" style={{minWidth: 150}}>
              <div className="disabled-select" title={showTooltip ? `Change this choice selection at the ${PARENT_REGION_TITLES[countryToRegionMap[record.region?.replace("US_", "STATE_")]]} level` : null}>
                <span className="text-item">{legalBasisIndexMap[value ?? "NOT_APPLICABLE"]}</span>
                <span className="arrow"><DownOutlined /></span> 
              </div>
            </div>
          )
        }
        return (
          <Tooltip
            title={isDisabled ? `Change this choice selection at the ${PARENT_REGION_TITLES[countryToRegionMap[record.region?.replace("US_", "STATE_")]]} level` : null}
          >
            <Select
              value={value}
              // defaultValue={!Boolean(value) ? "NOT_APPLICABLE" : null}
              placeholder="Multi-Select"
              options={LEGALBASIS_OPTIONS}
              disabled={isDisabled || props.readOnly}
              onChange={(value) => handleLegalBasisChange(cat.id, record.region, value, Boolean(record.children) ? record.region : null)}
              style={{minWidth: 150}}
            />
          </Tooltip>
        )
      }
    })
  })

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableState({pagination,filters,sorter, extra});
  };

  useEffect(() => {
    const buildRegionHierarchy = (treeStructure, appliesGeos, exclusionGeos) => {
      return treeStructure.map((region) => {
      // Filter children based on appliesGeos and exclusionGeos
      const children = region.children
        .filter((child) => {
        const isExcluded = exclusionGeos.includes(child.code);
        const isApplied = appliesGeos.includes(region.key) || appliesGeos.includes(child.code);
        return isApplied && !isExcluded;
        })
        .map((child) => {
        const codeString = child.code?.replace("STATE_", "US_");
        return {
          region: codeString,
          key: codeString,
          title: region_title_index_map[child.code],
          legalBasis: {}, // Initialize legalBasis
          privacySignal: privacySignalConfigByGeo[codeString]?.legislation ?? "usnat", // Default privacy signal
        };
        });
    
      // Include the region if it has valid children or is part of appliesGeos
      if (children.length > 0 || appliesGeos.includes(region.key)) {
        const codeString = region.code?.replace("STATE_", "US_");
        return {
        region: codeString,
        key: codeString,
        title: region.title,
        legalBasis: {}, // Initialize legalBasis
        privacySignal: ["US", "CA"].includes(codeString) ? "usnat" : "regional", // Default privacy signal
        children, // Preserve hierarchy
        };
      }
      return null; // Exclude regions that don't meet the criteria
      }).filter(Boolean); // Remove null entries
    };
    
    // Build the hierarchical data
    const hierarchicalData = buildRegionHierarchy(
      GEO_LIST_TREE_STRUCTURE,
      props.usPrivacyRegulation.appliesGeos,
      props.usPrivacyRegulation.exclusionGeos
    );
    
    // Map categories to the hierarchical data
    const mappedData = hierarchicalData.map((region) => {
      const mapCategories = (node) => {
      categories.forEach((cat) => {
        if (node.children) {
          // For parent nodes, get the value from configByGeo[REGION_X]
          let prefixCode = "";
          if(node.region === "EEA" || node.region === "OTHER") {
            prefixCode = "REGION_";
          }
          const regionKey = `${prefixCode}${node.region}`;
          const parentConfig = cat.configByGeo[regionKey];
          node.legalBasis[cat.id] = parentConfig?.defaultLegalBasis ?? "NOT_APPLICABLE";
        } else {
          // For child nodes, keep existing logic
          let defLegBasisRecord = cat.configByGeo[node.region];
          const parentRegionCode = countryToRegionMap[node.region?.replace("US_", "STATE_")]
          if(parentRegionCode.includes("REGION_") ? node.privacySignal === "regional" : node.privacySignal === "usnat") {
            defLegBasisRecord = cat.configByGeo[parentRegionCode];
          }
          if(defLegBasisRecord?.disclosureOnly) {
            defLegBasisRecord.defaultLegalBasis = "DISCLOSURE_ONLY";
          }
          if(defLegBasisRecord) {
            node.legalBasis[cat.id] = defLegBasisRecord?.defaultLegalBasis ?? "NOT_APPLICABLE";
          }
        }
      });
    
      if (node.children) {
        node.children = node.children.map(mapCategories);
      }
      return node;
      };
      
      return mapCategories(region);
    });
    
    setRegionCategoryMapping(mappedData);
    }, [
    categories,
    privacySignalConfigByGeo,
    props.usPrivacyRegulation.appliesGeos,
    props.usPrivacyRegulation.exclusionGeos,
    ]);

  function handleGpcEdit() {
    setOpenRespectGpcModal(true);
  }

  const scrollTable = (scrollBy) => {
    const tableBody = document.querySelector('.usnat-region-mapping-global-standard .ant-table-body');
    if (tableBody) {
      tableBody.scrollTo({
        left: tableBody.scrollLeft + scrollBy,
        behavior: 'smooth'
      });
    }
  };

  const updateButtonState = () => {
    const tableBody = document.querySelector('.usnat-region-mapping-global-standard .ant-table-body');
    if (tableBody) {
      const maxScrollLeft = tableBody.scrollWidth - tableBody.clientWidth;

      setIsLeftDisabled(tableBody.scrollLeft <= 0);
      setIsRightDisabled(tableBody.scrollLeft >= maxScrollLeft - 1);
    }
  };

 useEffect(() => {
    if (props.usPrivacyRegulation?.appliesGeos?.length === 0) return;

    // Using setTimeout to ensure table is mounted
    const timeoutId = setTimeout(() => {
      const tableBody = document.querySelector('.usnat-region-mapping-global-standard .ant-table-body');
      if (tableBody) {
        tableBody.addEventListener('scroll', updateButtonState);
        updateButtonState();
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      const tableBody = document.querySelector('.usnat-region-mapping-global-standard .ant-table-body');
      if (tableBody) {
        tableBody.removeEventListener('scroll', updateButtonState);
      }
    };
  }, [props.usPrivacyRegulation?.appliesGeos?.length]);

  const handleAddRegions = (updatedReg) => {
    setCategories(updatedReg.categories);
    setPrivacySignalConfigByGeo(updatedReg.configByGeo)
    props.usPrivacyRegulation.appliesGeos = updatedReg.appliesGeos;
    props.usPrivacyRegulation.exclusionGeos = updatedReg.exclusionGeos;
    setShowAddRegionsModal(false);
  };

  const debouncedChangeHandler = useDebouncedSearch(setSearchValue, 700);

  const dataSource = useMemo(() => {
    if (!searchValue.trim().length) {
      return regionCategoryMapping;
    }
    
    return regionCategoryMapping?.filter(record => {
      const parentMatch = record.title?.toLowerCase().includes(searchValue.trim().toLowerCase());
      const childrenMatch = record.children?.some(child => 
        child.title?.toLowerCase().includes(searchValue.trim().toLowerCase())
      );
      return parentMatch || childrenMatch;
    }).map(record => ({
      ...record,
      children: record.children?.filter(child =>
        child.title?.toLowerCase().includes(searchValue.trim().toLowerCase())
      )
    }));
  }, [searchValue, regionCategoryMapping]);

  // Update the useEffect to only depend on searchValue
  useEffect(() => {
    if (searchValue.trim().length) {
      setExpandedRowKeys(dataSource?.map(item => item.key) ?? []);
    } else {
      setExpandedRowKeys([]); // Reset when search is cleared
    }
  }, [searchValue]); // Remove dataSource dependency

  // Add expandable configuration for the Table
  const expandableConfig = useMemo(() => ({
    expandedRowKeys,
    onExpand: (expanded, record) => {
      setExpandedRowKeys(prev => {
        if (expanded) {
          return [...prev, record.key];
        } else {
          return prev.filter(key => key !== record.key);
        }
      });
    },
  }), [expandedRowKeys]);

  const gpcButton = categories.some((cat) => cat.purposeRef?.systemId === IAB_CHOICES_SYSTEM_IDS.saleAndSharing) ?  <Button icon={<SafetyOutlined/>} onClick={handleGpcEdit}>GPC</Button> : null;

  return systemPurposesPending ? <Spin/> :  (
    <>
      {props.changesDetectedWarning}
      <Row gutter={[0, token.margin]}>
        <Col span={24}>
          {!props.readOnly ? (
            <Space direction="vertical" size={token.marginXXS}>
              <Title level={4}>Region Mapping</Title>
              <Text>
                We have configured the default consent options for privacy choices on a per region basis which can be modified using the drop-down fields in the matrix below.
              </Text>
            </Space>
          ) : null}
        </Col>
        {props.usPrivacyRegulation.appliesGeos.length === 0
          ? (
          <Col span={24}>
            {props.showError ? <Typography.Text type="danger">{"Select at least one Region"}</Typography.Text> : null}
            <Flex justify="center" align="center" vertical={true} gap={token.marginXXS} style={{borderRadius: 5, border: props.showError ? `1px solid ${token.colorErrorBorder}` : 0, height: `500px`, background: 'white'}}>
              <Typography.Title level={5} type="secondary">You need to have at least one region to move onto next step.</Typography.Title>
              {!props.readOnly ? <Button type="primary" onClick={() => setShowAddRegionsModal(true)}>Add Regions</Button> : null}
            </Flex>
          </Col>
          ) : (
          <>
          <Col span={8}>
            <Input.Search
              placeholder="search"
              type="search"
              onChange={({ target: { value } }) => {
                  debouncedChangeHandler(value);
                }}
            />
          </Col>
          <Col span={8} offset={8}>
            <Flex align='center' justify="flex-end">
            <Space size={token.margin}>
              <>
                {gpcButton}
                {!props.readOnly ? <Button color="primary" onClick={() => setShowAddRegionsModal(true)}>Add Regions</Button> : null}
              </>
            </Space>
            <Divider type="vertical" /> 
            <Space size={token.margin}>
              <Button  disabled={isLeftDisabled} icon={<LeftOutlined/>} onClick={() => scrollTable(-1000)}></Button>
              <Button  disabled={isRightDisabled} icon={<RightOutlined/>} onClick={() => scrollTable(1000)}></Button>
            </Space>
            </Flex>
          </Col>
          <Col span={24} flex={5}>
            <Table
              onChange={handleTableChange}
              columns={columns}
              dataSource={dataSource}
              className="usnat-region-mapping-global-standard"
              scroll={{ x: 1000, y: 500}}
              expandable={expandableConfig}
              pagination={false}
            />
          </Col>    
          </>
          )
        }
      </Row>
      {/* {openBulkModal ? (
        <BulkRegionMappingModalGlobalStandard
          selectedRegions={regionCategoryMapping.reduce((acc, region) => {
            if (selectedRowKeys.includes(region.region)) {
              acc.push(region);
            } else if (region.children) {
              const selectedChildren = region.children.filter(child => selectedRowKeys.includes(child.region));
              acc.push(...selectedChildren);
            }
            return acc;
            }, [])
          }
          categories={categories}
          setCategories={setCategories}
          openModal={openBulkModal}
          closeModal={() => {setOpenBulkModal(false); setSelectedRowKeys([])}}
        />
      ) : null} */}
      {openRespectGpcModal ? (
        <RespectGpcModal
          respectGpcCategory={categories.find((cat) => cat.purposeRef?.systemId === IAB_CHOICES_SYSTEM_IDS.saleAndSharing) ?? {respectGPC: null}}
          regionsInVendorList={regionCategoryMapping.map((opt) => opt.children.map(o => o.region)).flat()}
          setCategories={setCategories}
          openModal={openRespectGpcModal}
          closeModal={() => setOpenRespectGpcModal(false)}
          readOnly={props.readOnly}
          isGlobalStandardFlow={true}
        />
      ) : null}
      {contextHolder}
      {showAddRegionsModal ?(
        <AddRegionsModal
          openRegionsModal={showAddRegionsModal}
          handleCancel={() => setShowAddRegionsModal(false)}
          addRegions={handleAddRegions}
          categories={categories}
          setCategories={setCategories}
          privacySignalConfigByGeo={privacySignalConfigByGeo}
          setPrivacySignalConfigByGeo={setPrivacySignalConfigByGeo}
          usPrivacyRegulation={props.usPrivacyRegulation}
          systemPurposesJS={systemPurposes?.toJS()}
          showError={props.showError}
          summaryPageData={props.summaryPageData}
        />
      ): null}
    </>
  );
}

export default RegionMappingGlobalStandard;